import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';

import AnnotatorConstants from '../annotator.constants';

const useStyles = makeStyles(() => ({
  validationSelectorContainer: {
    marginLeft: '8px',
    maxWidth: 100,
    paddingRight: '8px',
    display: 'flex',
    justifyContent: 'center',
  },
  validationFormControl: {
    margin: 0,
    minWidth: 100,
  },
  validationSelect: {
    fontSize: 12,
    '& > div': {
      padding: '8px 24px 8px 8px',
    },
  },
  validationSelectItem: {
    fontSize: 12,
    fontWeight: 400,
    display: 'block',
  },
  chartButton: {
    backgroundColor: 'blue',
    color: 'white',
    marginLeft: '140px',
    justifyContent: 'center',
  },
}));

const ValidationDropdown = (props: any) => {
  const {
    finding = {},
    onValidationReasonChange = () => {},
    isOnSidebar = false,
    isVideaDemoUser = false,
  } = props;
  const {
    validation = '',
    validationReason = '',
    validationReasons = [],
  } = finding;
  const classes = useStyles();

  const [placeHolder, reasons] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- 'placeHolder' is already declared in the upper scope on line 64 column 10.
    let placeHolder = 'Select validation';
    // eslint-disable-next-line @typescript-eslint/no-shadow -- 'reasons' is already declared in the upper scope on line 64 column 23.
    let reasons: any = [];
    if (validation === 'Agree' && isVideaDemoUser) {
      placeHolder = 'Tx plan';
      reasons = AnnotatorConstants.DEMO_REASONS;
    } else if (validation === 'Agree') {
      placeHolder = 'Tx plan';
      reasons = AnnotatorConstants.AGREE_REASONS;
    } else if (validation === 'Disagree') {
      placeHolder = 'Reason';
      reasons = AnnotatorConstants.DISAGREE_REASONS;
    } else if (validation === 'Monitor') {
      placeHolder = '-';
    }
    return [placeHolder, reasons];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useMemo has a missing dependency: 'isVideaDemoUser'. Either include it or remove the dependency array.
  }, [validation]);

  const [isOpen, setIsOpen] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (
      mountedRef.current &&
      validation.length &&
      validation !== 'Monitor' &&
      !isOnSidebar
    ) {
      setIsOpen(true);
    } else {
      mountedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'isOnSidebar'. Either include it or remove the dependency array.
  }, [validation]);

  // NOTE: Without this the placeholder is not showing properly
  const handleRenderSelectValue = () => {
    if (!validationReasons.length) {
      return placeHolder;
    }
    return validationReasons
      .map((reason: any) => {
        // @ts-expect-error TS(7006): Parameter 'r' implicitly has an 'any' type.
        const findReason = reasons.find((r) => r.value === reason);
        return findReason ? findReason.label : '';
      })
      .filter((reason: any) => reason && reason.length)
      .join(', ');
  };

  const handleSelectionChange = (event: any) => {
    setIsOpen(true);
    onValidationReasonChange(event);
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      className={classes.validationSelectorContainer}
    >
      <FormControl className={classes.validationFormControl} variant="outlined">
        <Select
          value={validationReason}
          onChange={handleSelectionChange}
          renderValue={handleRenderSelectValue}
          className={classes.validationSelect}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          disabled={!reasons.length}
          displayEmpty
        >
          <MenuItem value="" className={classes.validationSelectItem} disabled>
            {placeHolder}
          </MenuItem>
          {/** @ts-expect-error TS(7006): Parameter 'reason' implicitly has an 'any' type. */}
          {reasons.map((reason, index) => {
            const activeReason = validationReasons.find(
              (value: any) => value === reason.value,
            );
            return (
              <MenuItem
                key={index}
                value={reason.value}
                className={classes.validationSelectItem}
              >
                <Checkbox color="primary" checked={!!activeReason} />{' '}
                {reason.label}
              </MenuItem>
            );
          })}
          {isVideaDemoUser && (
            <Button
              className={classes.chartButton}
              color="primary"
              variant="outlined"
            >
              Chart
            </Button>
          )}
          <MenuItem value="" style={{ display: 'none' }} />
        </Select>
      </FormControl>
    </Box>
  );
};

ValidationDropdown.propTypes = {
  finding: PropTypes.object,
  onValidationReasonChange: PropTypes.func,
  isOnSidebar: PropTypes.bool,
  isVideaDemoUser: PropTypes.bool,
};

export default ValidationDropdown;
